<template>
	<div class="min-h-screen bg-gray-50 p-3 sm:p-4 md:p-6 lg:p-8">
		<div class="max-w-4xl mx-auto">
			<!-- Header Section -->
			<div class="text-center mb-6 lg:mb-8">
				<div
					class="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 bg-primary rounded-xl mb-3 sm:mb-4 shadow-sm"
				>
					<i class="fas fa-shield-alt text-white"></i>
				</div>
				<h1
					class="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-2 sm:mb-3"
				>
					KYC Verification
				</h1>
				<p
					class="text-sm sm:text-base text-gray-600 max-w-3xl mx-auto px-2 sm:px-0"
				>
					Please provide your personal information for verification
					and account setup
				</p>
			</div>

			<form
				@submit.prevent="submitDetails"
				class="space-y-4 sm:space-y-6"
			>
				<!-- KYC Information Card -->
				<div
					class="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-5"
				>
					<div class="flex items-center mb-3 sm:mb-4">
						<div
							class="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 bg-blue-100 rounded-lg mr-2 sm:mr-3"
						>
							<i class="fas fa-user text-blue-600"></i>
						</div>
						<h3
							class="text-sm sm:text-base font-semibold text-gray-800"
						>
							Personal Information
						</h3>
					</div>

					<div class="grid grid-cols-1 gap-3 sm:gap-4">
						<!-- Phone Number -->
						<div>
							<label
								for="phoneNumber"
								class="flex items-center text-xs sm:text-sm font-medium text-gray-700 mb-1"
							>
								Phone Number
								<span
									class="text-red-500 ml-1"
									v-if="errors.phoneNumber"
									>*</span
								>
							</label>
							<div class="relative">
								<span
									class="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 pointer-events-none"
								>
									<i class="fas fa-phone"></i>
								</span>
								<input
									type="tel"
									id="phoneNumber"
									v-model="kycDetails.phoneNumber"
									@blur="validateField('phoneNumber')"
									required
									placeholder="e.g., 0712 345 678"
									class="w-full pl-11 pr-3 bg-gray-50 border rounded-lg py-2 text-sm text-gray-700 transition-all duration-200 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary hover:border-gray-400"
									:class="{
										'border-red-500': errors.phoneNumber,
									}"
									aria-describedby="phoneNumber-error"
								/>
							</div>
							<p
								v-if="errors.phoneNumber"
								id="phoneNumber-error"
								class="text-xs text-red-500 mt-1"
							>
								{{ errors.phoneNumber }}
							</p>
							<p class="mt-1 text-xs text-gray-500">
								We'll use this for important notifications
							</p>
						</div>

						<!-- KRA PIN -->
						<div>
							<label
								for="kraPin"
								class="flex items-center text-xs sm:text-sm font-medium text-gray-700 mb-1"
							>
								KRA PIN
								<span
									class="text-red-500 ml-1"
									v-if="errors.kraPin"
									>*</span
								>
							</label>
							<div class="relative">
								<span
									class="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 pointer-events-none"
								>
									<i class="fas fa-file-alt"></i>
								</span>
								<input
									type="text"
									id="kraPin"
									v-model="kycDetails.kraPin"
									@blur="validateField('kraPin')"
									required
									placeholder="e.g., A123456789X"
									class="w-full pl-11 pr-3 bg-gray-50 border rounded-lg py-2 text-sm text-gray-700 transition-all duration-200 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary hover:border-gray-400"
									:class="{ 'border-red-500': errors.kraPin }"
									aria-describedby="kraPin-error"
								/>
							</div>
							<p
								v-if="errors.kraPin"
								id="kraPin-error"
								class="text-xs text-red-500 mt-1"
							>
								{{ errors.kraPin }}
							</p>
							<p class="mt-1 text-xs text-gray-500">
								Your Kenya Revenue Authority Personal
								Identification Number
							</p>
						</div>

						<!-- Account Type -->
						<div>
							<label
								for="clientType"
								class="flex items-center text-xs sm:text-sm font-medium text-gray-700 mb-1"
							>
								Account Type
								<span
									class="text-red-500 ml-1"
									v-if="errors.clientType"
									>*</span
								>
							</label>
							<div class="relative">
								<span
									class="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 pointer-events-none"
								>
									<i class="fas fa-building"></i>
								</span>
								<select
									id="clientType"
									v-model="kycDetails.clientType"
									@blur="validateField('clientType')"
									required
									class="w-full pl-11 pr-8 appearance-none bg-gray-50 border rounded-lg py-2 text-sm text-gray-700 transition-all duration-200 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary hover:border-gray-400"
									:class="{
										'border-red-500': errors.clientType,
									}"
									aria-describedby="clientType-error"
								>
									<option value="select" disabled>
										Select account type
									</option>
									<option value="INDIVIDUAL">
										Individual
									</option>
									<option value="CORPORATE">Corporate</option>
								</select>
								<div
									class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"
								>
									<i
										class="fas fa-chevron-down text-gray-400"
									></i>
								</div>
							</div>
							<p
								v-if="errors.clientType"
								id="clientType-error"
								class="text-xs text-red-500 mt-1"
							>
								{{ errors.clientType }}
							</p>
						</div>

						<!-- Physical Address -->
						<div>
							<label
								for="address"
								class="flex items-center text-xs sm:text-sm font-medium text-gray-700 mb-1"
							>
								Physical Address
								<span
									class="text-red-500 ml-1"
									v-if="errors.address"
									>*</span
								>
							</label>
							<div class="relative">
								<span
									class="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 pointer-events-none"
								>
									<i class="fas fa-home"></i>
								</span>
								<input
									type="text"
									id="address"
									v-model="kycDetails.address"
									@blur="validateField('address')"
									required
									placeholder="Enter your complete address"
									class="w-full pl-11 pr-3 bg-gray-50 border rounded-lg py-2 text-sm text-gray-700 transition-all duration-200 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary hover:border-gray-400"
									:class="{
										'border-red-500': errors.address,
									}"
									aria-describedby="address-error"
								/>
							</div>
							<p
								v-if="errors.address"
								id="address-error"
								class="text-xs text-red-500 mt-1"
							>
								{{ errors.address }}
							</p>
							<p class="mt-1 text-xs text-gray-500">
								Your current residential or business address
							</p>
						</div>
					</div>
				</div>

				<!-- Action Buttons -->
				<div
					class="flex flex-col-reverse sm:flex-row justify-between gap-3 sm:gap-4"
				>
					<button
						@click="goBack"
						class="w-full sm:w-auto flex items-center justify-center px-4 py-2.5 text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-md font-medium transition-colors duration-200 focus:outline-none focus:ring-1 focus:ring-gray-400 focus:ring-offset-1 text-sm sm:text-base"
					>
						<i class="fas fa-arrow-left mr-1"></i>
						Back
					</button>

					<button
						type="submit"
						:disabled="isFormInvalid"
						class="w-full sm:w-auto bg-primary hover:bg-primary-dark disabled:bg-gray-400 text-white font-medium py-2.5 px-4 rounded-md transition-colors duration-200 focus:outline-none focus:ring-1 focus:ring-primary focus:ring-offset-1 disabled:cursor-not-allowed text-sm sm:text-base"
					>
						Submit KYC Details
					</button>
				</div>
			</form>
		</div>
	</div>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex';
import { UpdateClient } from '../../../api/auth';

export default {
	name: 'KYCDetailsForm',
	data() {
		return {
			hasAttemptedSubmit: false,
			errors: {
				phoneNumber: '',
				kraPin: '',
				clientType: '',
				address: '',
			},
		};
	},
	computed: {
		...mapState('insurance/motorPrivate', ['kycDetails']),
		...mapState('auth', ['authData']),
		isFormInvalid() {
			// Only check for form validity after submission attempt
			if (!this.hasAttemptedSubmit) return false;

			return (
				Object.values(this.errors).some((error) => error !== '') ||
				!this.kycDetails.phoneNumber ||
				!this.kycDetails.kraPin ||
				!this.kycDetails.clientType ||
				this.kycDetails.clientType === 'select' ||
				!this.kycDetails.address
			);
		},
	},
	mounted() {
		this.autofillFromAuthData();
	},
	methods: {
		...mapMutations('insurance/motorPrivate', [
			'UPDATE_KYC_DETAILS',
			'CLEAR_ERRORS',
		]),
		...mapActions('risk', ['setKycData']),

		autofillFromAuthData() {
			if (this.authData && this.authData.user) {
				const user = this.authData.user;
				const updatedDetails = { ...this.kycDetails };

				if (user.phone && !updatedDetails.phoneNumber) {
					updatedDetails.phoneNumber = user.phone;
				}

				this.UPDATE_KYC_DETAILS(updatedDetails);
				// Don't validate pre-filled fields immediately - only after submission attempt
			}
		},

		goBack() {
			this.$router.push({ name: 'VehicleDetailsForm' });
		},

		kycData() {
			return {
				kraPin: this.kycDetails.kraPin,
				phone: this.kycDetails.phoneNumber,
				address: this.kycDetails.address,
				clientType: this.kycDetails.clientType,
			};
		},

		validateField(field) {
			// Only show real-time errors after user has attempted to submit
			if (!this.hasAttemptedSubmit) return;

			this.errors[field] = '';

			switch (field) {
				case 'phoneNumber':
					if (!this.kycDetails.phoneNumber) {
						this.errors.phoneNumber = 'Phone number is required';
					} else if (
						!/^(?:\+254|0)[1-9]\d{8}$/.test(
							this.kycDetails.phoneNumber,
						)
					) {
						this.errors.phoneNumber =
							'Please enter a valid Kenyan phone number (e.g., 0712345678 or +254712345678)';
					}
					break;
				case 'kraPin':
					if (!this.kycDetails.kraPin) {
						this.errors.kraPin = 'KRA PIN is required';
					} else if (
						!/^[A-Z]\d{9}[A-Z]$/.test(this.kycDetails.kraPin)
					) {
						this.errors.kraPin =
							'Please enter a valid KRA PIN (e.g., A123456789X)';
					}
					break;
				case 'clientType':
					if (
						!this.kycDetails.clientType ||
						this.kycDetails.clientType === 'select'
					) {
						this.errors.clientType = 'Account type is required';
					}
					break;
				case 'address':
					if (!this.kycDetails.address) {
						this.errors.address = 'Physical address is required';
					}
					break;
			}
		},

		validateAllFields() {
			const fields = ['phoneNumber', 'kraPin', 'clientType', 'address'];

			fields.forEach((field) => {
				this.validateField(field);
			});

			return !Object.values(this.errors).some((error) => error !== '');
		},

		async submitDetails() {
			// Mark that user has attempted to submit
			this.hasAttemptedSubmit = true;

			// Validate all fields
			if (!this.validateAllFields()) {
				Event.$emit('notifications:show', {
					type: 'error',
					title: 'Form Validation Error',
					message:
						'Please correct the highlighted errors before submitting.',
					duration: 4000,
				});
				return;
			}

			// Check for required fields
			const requiredFields = [
				{
					field: 'phoneNumber',
					label: 'Phone Number',
					value: this.kycDetails.phoneNumber,
				},
				{
					field: 'kraPin',
					label: 'KRA PIN',
					value: this.kycDetails.kraPin,
				},
				{
					field: 'clientType',
					label: 'Account Type',
					value: this.kycDetails.clientType,
				},
				{
					field: 'address',
					label: 'Physical Address',
					value: this.kycDetails.address,
				},
			];

			const missingFields = requiredFields.filter(
				(field) =>
					!field.value ||
					(field.field === 'clientType' && field.value === 'select'),
			);

			if (missingFields.length > 0) {
				const fieldNames = missingFields.map((field) => field.label);
				Event.$emit('notifications:show', {
					type: 'error',
					title: 'Incomplete Form',
					message: `Please fill in: ${fieldNames.join(', ')}`,
					duration: 4000,
				});
				return;
			}

			const kycData = this.kycData();
			this.UPDATE_KYC_DETAILS(kycData);

			try {
				const res = await UpdateClient(kycData);
				if (res.status) {
					this.CLEAR_ERRORS();
					Event.$emit('notifications:show', {
						type: 'success',
						title: 'KYC Details Saved',
						message:
							'Your KYC details have been successfully submitted.',
						duration: 3000,
					});
					this.$router.push({ name: 'SummaryPage' });
				} else {
					throw new Error('Failed to update KYC details');
				}
			} catch (err) {
				let errorMessage =
					(err.response &&
						err.response.data &&
						err.response.data.errors) ||
					'Failed to submit KYC details';
				Event.$emit('notifications:show', {
					type: 'error',
					title: 'Submission Error',
					message: `${errorMessage}. Please try again.`,
					duration: 4000,
				});
			}

			this.setKycData(kycData);
		},
	},
};
</script>

<style scoped>
@keyframes fade-in-up {
	from {
		opacity: 0;
		transform: translateY(20px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

.animate-fade-in-up {
	animation: fade-in-up 0.4s ease-out forwards;
}

/* Reduced motion preferences */
@media (prefers-reduced-motion: reduce) {
	.animate-fade-in-up {
		animation: none;
	}
}

/* Error state animations */
.border-red-500 {
	transition: border-color 0.3s ease;
}

/* Improved mobile form spacing */
@media (max-width: 640px) {
	.max-w-4xl {
		width: 100%;
	}

	select,
	input {
		font-size: 16px; /* Prevents zoom on iOS */
		min-height: 44px; /* Minimum touch target size */
	}

	button {
		min-height: 48px; /* Minimum touch target size */
	}
}
</style>
